import React, { useCallback, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../../components/atoms';
import { DataTable } from '../../components/organisms';
import { DeleteConfirmationModal } from '../../components/organisms/DeleteConfirmationModal';
import { useSongs, useSongActions } from '../../hooks/useSongs';
import type { CreateTrackRequest, Song, UpdateTrackRequest } from '../../types/api/song';
import type { Column, TableAction } from '../../types/table';
import { SongFormModal } from './components/SongFormModal';

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

export const SongManagementPage: React.FC = () => {
  const { fetchSongs } = useSongs();
  const { createTrack, updateTrack, getTrack, deleteTrack } = useSongActions();

  const refreshKeyRef = useRef(0);
  const [selectedSong, setSelectedSong] = useState<Song | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleRefresh = useCallback(() => {
    refreshKeyRef.current += 1;
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedSong(null);
    setIsCreateModalOpen(true);
  }, []);

  const handleEdit = useCallback(
    async (song: Song) => {
      try {
        const full = await getTrack(song.trackId);
        setSelectedSong(full);
        setIsEditModalOpen(true);
      } catch (error: unknown) {
        toast.error(getErrorMessage(error, 'Failed to load track details'));
      }
    },
    [getTrack]
  );

  const handleDelete = useCallback((song: Song) => {
    setSelectedSong(song);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCreateSubmit = useCallback(
    async (data: CreateTrackRequest | UpdateTrackRequest, audioFile?: File | null) => {
      try {
        await createTrack(data as CreateTrackRequest, audioFile || undefined);
        toast.success('Track created successfully');
        setIsCreateModalOpen(false);
        handleRefresh();
      } catch (error: unknown) {
        toast.error(getErrorMessage(error, 'Failed to create track'));
      }
    },
    [createTrack, handleRefresh]
  );

  const handleEditSubmit = useCallback(
    async (data: CreateTrackRequest | UpdateTrackRequest, audioFile?: File | null) => {
      if (!selectedSong) return;
      try {
        await updateTrack(selectedSong.trackId, data as UpdateTrackRequest, audioFile || undefined);
        toast.success('Track updated successfully');
        setIsEditModalOpen(false);
        setSelectedSong(null);
        handleRefresh();
      } catch (error: unknown) {
        toast.error(getErrorMessage(error, 'Failed to update track'));
      }
    },
    [selectedSong, updateTrack, handleRefresh]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedSong) return;
    try {
      await deleteTrack(selectedSong.trackId);
      toast.success('Track deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedSong(null);
      handleRefresh();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Failed to delete track'));
    }
  }, [selectedSong, deleteTrack, handleRefresh]);

  const columns = useMemo<Column<Song>[]>(
    () => [
      { id: 'title', header: 'Title', accessor: 'title', minWidth: '200px' },
      { id: 'artist', header: 'Artist', accessor: 'artistName' },
      { id: 'album', header: 'Album', accessor: 'albumName', cell: (r) => r.albumName || '—' },
      {
        id: 'duration',
        header: 'Duration',
        accessor: 'durationSeconds',
        cell: (r) => {
          const m = Math.floor(r.durationSeconds / 60);
          const s = r.durationSeconds % 60;
          return `${m}:${String(s).padStart(2, '0')}`;
        },
      },
      { id: 'status', header: 'Status', accessor: 'status' },
    ],
    []
  );

  const actions = useMemo<TableAction<Song>[]>(
    () => [
      {
        id: 'edit',
        label: 'Edit',
        variant: 'ghost',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        ),
        onClick: handleEdit,
      },
      {
        id: 'delete',
        label: 'Delete',
        variant: 'danger',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        ),
        onClick: handleDelete,
      },
    ],
    [handleEdit, handleDelete]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-left">
          <h1 className="text-lg font-bold text-gray-900">Songs / Tracks</h1>
          <p className="text-gray-600 mt-1 pl-3">Manage music tracks for the SAUNG streaming catalog</p>
        </div>
        <Button
          onClick={handleCreate}
          leftIcon={
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
          }
        >
          Add Track
        </Button>
      </div>

      <DataTable<Song>
        key={refreshKeyRef.current}
        columns={columns}
        fetchData={fetchSongs}
        searchable
        searchPlaceholder="Search songs..."
        initialPageSize={10}
        pageSizeOptions={[10, 20, 50, 100]}
        actions={actions}
        hoverable
        showRefreshButton
        onRefresh={handleRefresh}
        caption="Track management table"
      />

      <SongFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateSubmit}
        mode="create"
      />

      <SongFormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedSong(null);
        }}
        onSubmit={handleEditSubmit}
        mode="edit"
        song={selectedSong || undefined}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedSong(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Track"
        message={
          selectedSong
            ? `Are you sure you want to delete "${selectedSong.title}"? This action cannot be undone.`
            : 'Are you sure you want to delete this track?'
        }
      />
    </div>
  );
};

export default SongManagementPage;
